import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TID } from "@/lib/testIds";
import { ShieldAlert } from "lucide-react";

/**
 * TwoFactorModal — high-security prompt for irreversible actions.
 * Scaffold mock: the expected code is exposed via REACT_APP_SECOND_FACTOR_CODE
 * (default "000000") and displayed on-screen so the flow is testable.
 */
export default function TwoFactorModal({ open, onOpenChange, onConfirm, actionLabel }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const expected = process.env.REACT_APP_SECOND_FACTOR_CODE || "000000";

  useEffect(() => {
    if (!open) {
      setCode("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (code.trim() !== expected) {
      setError("Codice non valido. Azione non eseguita.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(code.trim());
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.twoFaModal}
        className="rounded-none border border-[#F5A623]/60 bg-[#0C0C0C] text-[#EDEDED] max-w-md p-0"
      >
        <div className="border-b border-[#1F1F1F] px-5 py-3 flex items-center gap-2 bg-[#141414]">
          <ShieldAlert className="w-4 h-4 text-[#F5A623]" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#F5A623]">
            System override // second factor required
          </span>
        </div>

        <div className="px-5 py-5 space-y-4">
          <DialogHeader className="space-y-2 p-0">
            <DialogTitle className="font-heading text-xl uppercase tracking-tight">
              Azione irreversibile
            </DialogTitle>
            <DialogDescription className="font-mono text-xs text-neutral-400 leading-relaxed">
              {actionLabel ? `> ${actionLabel}` : "> Confermare l'operazione."} <br />
              Inserisci il codice a secondo fattore per procedere.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
              Second factor code
            </label>
            <Input
              data-testid={TID.twoFaInput}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError("");
              }}
              placeholder="000000"
              autoFocus
              inputMode="numeric"
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono tracking-[0.4em] text-center text-lg h-12 focus-visible:ring-0 focus-visible:border-[#F5A623]"
            />
            <div className="font-mono text-[10px] text-neutral-600">
              (scaffold) codice atteso: <span className="text-neutral-400">{expected}</span>
            </div>
            {error && (
              <div data-testid={TID.twoFaError} className="font-mono text-xs text-red-400">
                {error}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-[#1F1F1F] px-5 py-3 gap-2">
          <Button
            data-testid={TID.twoFaCancel}
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none font-mono text-xs uppercase tracking-widest hover:bg-[#141414]"
          >
            Annulla
          </Button>
          <Button
            data-testid={TID.twoFaConfirm}
            onClick={handleConfirm}
            disabled={submitting || code.length < 4}
            className="rounded-none bg-[#F5A623] hover:bg-[#F5A623]/90 text-black font-mono text-xs uppercase tracking-widest"
          >
            {submitting ? "Committing…" : "Conferma & Commit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
